import * as React from "react";
import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import { AntDTable } from "@/common/components/ui";
import { Card, CardContent } from "@/common/components/ui/card";
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  Image as ImageIcon,
  RefreshCw,
  Download,
  Eye,
} from "lucide-react";
import { Switch, TableColumnsType } from "antd";
import { useQuery } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import BannerService from "@/services/FinEaseHomeAndAdmin/APIs/BannerService";
import { Banner, BannerResponse } from "@/utils/types/Banner/Banner";
import { notify } from "@/common/components/common/Notification";
import DeleteConfirmationModal from "@/projects/FinEaseHomeAndAdmin/components/DeleteConfirmationModal";

const EXPORT_PAGE_SIZE = 100;
const MAX_EXPORT_PAGES = 500;

async function fetchAllBannersForExport(
  enabled: boolean,
  search?: string
): Promise<Banner[]> {
  const acc: Banner[] = [];
  for (let p = 1; p <= MAX_EXPORT_PAGES; p += 1) {
    const res = await BannerService.getBanners(
      p,
      EXPORT_PAGE_SIZE,
      enabled,
      search
    );
    const batch = res.results ?? [];
    acc.push(...batch);
    if (batch.length < EXPORT_PAGE_SIZE) {
      break;
    }
  }
  const q = (search ?? "").trim().toLowerCase();
  if (!q) {
    return acc;
  }
  return acc.filter(banner => (banner.title ?? "").toLowerCase().includes(q));
}

const BannersPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    const id = window.setTimeout(
      () => setDebouncedSearch(searchTerm.trim()),
      300
    );
    return () => window.clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const {
    data: bannersResponse,
    isLoading,
    isFetching,
    refetch,
  } = useQuery<BannerResponse>({
    queryKey: ["banners", page, pageSize, enabled, debouncedSearch],
    queryFn: () =>
      BannerService.getBanners(
        page,
        pageSize,
        enabled,
        debouncedSearch || undefined
      ),
    enabled: !!page && !!pageSize,
  });

  const total = bannersResponse?.count ?? 0;
  const filteredBanners = useMemo(() => {
    const rows = bannersResponse?.results ?? [];
    const q = debouncedSearch.toLowerCase();
    if (!q) {
      return rows;
    }
    return rows.filter(banner =>
      (banner.title ?? "").toLowerCase().includes(q)
    );
  }, [bannersResponse?.results, debouncedSearch]);

  const handleDeleteClick = (id: string, title: string) => {
    setBannerToDelete({ id, title });
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bannerToDelete) {
      return;
    }

    try {
      setActionLoading(true);
      const res: any = await BannerService.deleteBanner(bannerToDelete.id);
      if (res.success) {
        notify.success(
          res?.message ??
            `${bannerToDelete.title} has been deleted successfully.`
        );
      } else {
        notify.error(
          res?.message ??
            `${bannerToDelete.title} has been deleted successfully.`
        );
      }
      setDeleteModalOpen(false);
      setBannerToDelete(null);
      refetch();
    } catch (error: any) {
      console.error("Error deleting banner:", error);
      notify.error(
        error?.response?.data?.message ??
          error?.message ??
          "Failed to delete banner"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setBannerToDelete(null);
  };

  const handleSyncData = async () => {
    try {
      const result = await refetch();
      if (result.isError) {
        notify.error(
          "Sync failed",
          (result.error as Error)?.message ?? "Could not refresh banners."
        );
        return;
      }
      notify.success("Synced", "Banner list updated from server.");
    } catch (e: unknown) {
      notify.error(
        "Sync failed",
        e instanceof Error ? e.message : "Could not refresh banners."
      );
    }
  };

  const handleExportXlsx = async () => {
    setExportLoading(true);
    try {
      let exportList: Banner[];

      if (selectedRowKeys.length > 0) {
        // Export only selected rows (already in memory)
        const selectedSet = new Set(selectedRowKeys.map(String));
        exportList = filteredBanners.filter(b => selectedSet.has(String(b.id)));
        if (exportList.length === 0) {
          notify.error("No data", "Selected rows not found in current view.");
          return;
        }
      } else {
        // Export all (existing behaviour)
        exportList = await fetchAllBannersForExport(
          enabled,
          debouncedSearch || undefined
        );
        if (exportList.length === 0) {
          notify.error("No data", "No banners to export.");
          return;
        }
      }

      const rows = exportList.map(banner => ({
        Title: banner.title ?? "",
        "Start Date": banner.start_date ?? "",
        "End Date": banner.end_date ?? "",
        Link: banner.redirection_link ?? "",
        Status: banner.is_enabled ? "Enabled" : "Disabled",
        "Image URL": banner.banner_image_url ?? "",
        "Created On": banner.created_on ?? "",
        "Updated On": banner.updated_on ?? "",
      }));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, "Banners");
      const datePart = new Date().toISOString().split("T")[0];
      XLSX.writeFile(wb, `banners_${datePart}.xlsx`);
      notify.success(
        "Exported",
        `${rows.length} banner(s) downloaded as Excel.`
      );
    } catch (e: unknown) {
      notify.error(
        "Export failed",
        e instanceof Error ? e.message : "Could not export banners."
      );
    } finally {
      setExportLoading(false);
    }
  };

  const columns: TableColumnsType<Banner> = useMemo(
    () => [
      {
        title: "Image",
        dataIndex: "banner_image_url",
        key: "banner_image_url",
        width: 100,
        render: (_, banner) => (
          <div className="flex h-12 w-12 items-center justify-center rounded bg-gray-100 text-gray-400">
            <img
              src={banner.banner_image_url ?? ""}
              alt="Banner"
              className="h-12 w-12 rounded-lg object-cover"
            />
          </div>
        ),
      },
      {
        title: "Title",
        dataIndex: "title",
        key: "title",
        width: 200,
        render: text => <p className="font-bold text-secondary">{text}</p>,
      },
      {
        title: "Start Date",
        dataIndex: "start_date",
        key: "start_date",
        width: 150,
      },
      {
        title: "End Date",
        dataIndex: "end_date",
        key: "end_date",
        width: 150,
      },
      {
        title: "Link",
        dataIndex: "redirection_link",
        key: "redirection_link",
        width: 200,
      },
      {
        title: "Status",
        dataIndex: "is_enabled",
        key: "is_enabled",
        width: 100,
        render: (enabled: boolean) => (
          <span
            className={`rounded-full border px-3 py-1 text-xs font-medium ${
              enabled
                ? "border-green-200 bg-green-100 text-green-700"
                : "border-red-200 bg-red-100 text-red-700"
            }`}
          >
            {enabled ? "Enabled" : "Disabled"}
          </span>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        fixed: "right" as const,
        width: 120,
        render: (_, banner) => (
          <div className="float-right flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/admin/cms/banners/view/${banner.id}`)}
              className="h-8 w-8 rounded-lg text-slate-500"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/admin/cms/banners/edit/${banner.id}`)}
              className="h-8 w-8 rounded-lg text-slate-500"
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDeleteClick(banner.id, banner.title)}
              className="h-8 w-8 rounded-lg text-red-600"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [navigate]
  );

  return (
    <div className="space-y-6 p-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
        <svg
          className="absolute right-0 top-0 h-full w-1/3 text-white/5"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon fill="currentColor" points="50,0 100,0 100,100 0,100" />
        </svg>
        <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
              <ImageIcon className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-white">
                Banners
              </h1>
              <p className="text-white/70">Manage your website banners here.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={() => void handleSyncData()}
              disabled={isFetching}
            >
              <RefreshCw className="h-4 w-4" /> Sync Data
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={() => void handleExportXlsx()}
              disabled={exportLoading || selectedRowKeys.length === 0}
            >
              <Download className="h-4 w-4" />
              Export
              {selectedRowKeys.length > 0 && (
                <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
                  {selectedRowKeys.length}
                </span>
              )}
            </Button>
            <Button
              onClick={() => navigate("/admin/cms/banners/add")}
              className="gap-2 bg-white text-secondary hover:bg-white/90"
            >
              <Plus className="h-4 w-4" /> Add Banner
            </Button>
          </div>
        </div>
      </div>

      <Card className="rounded-xl border-none shadow-sm">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search banners by title..."
                className="h-11 rounded-xl bg-gray-50 pl-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">
                {enabled ? "Enabled" : "Disabled"}
              </span>
              <Switch
                checked={enabled}
                onChange={checked => setEnabled(checked)}
                style={{ backgroundColor: enabled ? "#175d80" : "#9ca3af" }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <AntDTable
        data={filteredBanners}
        columns={columns}
        rowKey="id"
        loading={isLoading || isFetching}
        showSettings
        rowSelection={{
          type: "checkbox",
          selectedRowKeys,
          onChange: keys => setSelectedRowKeys(keys),
          preserveSelectedRowKeys: false,
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPageSize(nextSize);
          },
        }}
        emptyText="No banners found"
      />

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Banner"
        message="Are you sure you want to delete"
        itemName={bannerToDelete?.title ?? ""}
        isLoading={actionLoading}
        confirmText="Yes, Delete"
        cancelText="No"
      />
    </div>
  );
};

export default BannersPage;
